// src/SuperAdminAiCache.js
import React, { useEffect, useMemo, useState } from 'react';
import API from './api';

function prettyJson(value) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value || '');
  }
}

const UI = {
  card: 'rounded-2xl border border-slate-200 bg-white shadow-sm',
  cardSoft: 'rounded-2xl border border-slate-200/80 bg-white/95 shadow-sm',
  input:
    'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50 disabled:text-slate-400',
  select:
    'w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-50 disabled:text-slate-400',
  label: 'mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-slate-500',
  th: 'px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500',
  td: 'px-3 py-2.5 align-top text-sm text-slate-700',
  smallBtn:
    'inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-50',
  primaryBtn:
    'inline-flex items-center justify-center rounded-xl bg-sky-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60',
  secondaryBtn:
    'inline-flex items-center justify-center rounded-xl bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60',
  ghostBtn:
    'inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60',
};

function Badge({ children, className = '' }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-5 ${className}`}
    >
      {children}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = String(status || '').toLowerCase();

  if (s === 'ready') {
    return (
      <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">
        {status}
      </Badge>
    );
  }
  if (s === 'pending') {
    return (
      <Badge className="border-amber-200 bg-amber-50 text-amber-700">
        {status}
      </Badge>
    );
  }
  if (s === 'failed') {
    return (
      <Badge className="border-rose-200 bg-rose-50 text-rose-700">
        {status}
      </Badge>
    );
  }
  if (s === 'queued') {
    return (
      <Badge className="border-sky-200 bg-sky-50 text-sky-700">
        {status}
      </Badge>
    );
  }
  if (s === 'running') {
    return (
      <Badge className="border-violet-200 bg-violet-50 text-violet-700">
        {status}
      </Badge>
    );
  }
  if (s === 'done') {
    return (
      <Badge className="border-emerald-200 bg-emerald-50 text-emerald-700">
        {status}
      </Badge>
    );
  }
  if (s === 'stale') {
    return (
      <Badge className="border-orange-200 bg-orange-50 text-orange-700">
        {status}
      </Badge>
    );
  }

  return (
    <Badge className="border-slate-200 bg-slate-50 text-slate-700">
      {status || '-'}
    </Badge>
  );
}

function TypeBadge({ type }) {
  const t = String(type || '').toLowerCase();

  if (t === 'visit_guidance') {
    return (
      <Badge className="border-sky-200 bg-sky-50 text-sky-700">
        Guidance
      </Badge>
    );
  }
  if (t === 'visit_note') {
    return (
      <Badge className="border-indigo-200 bg-indigo-50 text-indigo-700">
        Notes
      </Badge>
    );
  }

  return (
    <Badge className="border-slate-200 bg-slate-50 text-slate-700">
      {type || '-'}
    </Badge>
  );
}

function StatCard({ label, value, valueClassName = 'text-slate-900' }) {
  return (
    <div className={`${UI.cardSoft} p-3.5`}>
      <div className="text-[11px] font-medium uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className={`mt-1 text-xl font-bold ${valueClassName}`}>{value}</div>
    </div>
  );
}

function JsonModal({ open, onClose, title, data }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 p-3">
      <div className="max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-slate-800">
              {title}
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-3 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-800"
          >
            ✕
          </button>
        </div>

        <div className="max-h-[80vh] overflow-auto p-4">
          <pre className="whitespace-pre-wrap break-words rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs leading-5 text-slate-700">
            {prettyJson(data)}
          </pre>
        </div>
      </div>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className={`${UI.card} p-10 text-center text-sm text-slate-400`}>
      {text}
    </div>
  );
}

export default function SuperAdminAiCache() {
  const [tab, setTab] = useState('artifacts');

  const [orgOptions, setOrgOptions] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [orgTodayDate, setOrgTodayDate] = useState('');

  const [artifactRows, setArtifactRows] = useState([]);
  const [artifactLoading, setArtifactLoading] = useState(false);
  const [artifactType, setArtifactType] = useState('');
  const [artifactStatus, setArtifactStatus] = useState('');
  const [artifactVisitId, setArtifactVisitId] = useState('');

  const [versionRows, setVersionRows] = useState([]);
  const [versionLoading, setVersionLoading] = useState(false);
  const [versionType, setVersionType] = useState('');
  const [versionVisitId, setVersionVisitId] = useState('');

  const [jobRows, setJobRows] = useState([]);
  const [jobLoading, setJobLoading] = useState(false);
  const [jobType, setJobType] = useState('');
  const [jobStatus, setJobStatus] = useState('');
  const [jobVisitId, setJobVisitId] = useState('');

  const [jsonModal, setJsonModal] = useState({
    open: false,
    title: '',
    data: null,
  });

  const selectedOrg = useMemo(() => {
    return orgOptions.find((o) => String(o.id) === String(selectedOrgId)) || null;
  }, [orgOptions, selectedOrgId]);

  const canQuery = !!selectedOrgId && !!selectedDate;

  const openJson = (title, data) => {
    setJsonModal({
      open: true,
      title,
      data,
    });
  };

  const closeJson = () => {
    setJsonModal({
      open: false,
      title: '',
      data: null,
    });
  };

  const loadOrganizations = async () => {
    setOrgLoading(true);
    try {
      const res = await API.get('/super/organizations/options');
      const rows = res.data?.rows || [];
      setOrgOptions(rows);

      if (!selectedOrgId && rows.length) {
        const first = rows[0];
        setSelectedOrgId(String(first.id));
        setOrgTodayDate(first.server_today_date || '');
        setSelectedDate(first.server_today_date || '');
      }
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || 'Failed to load organizations');
    } finally {
      setOrgLoading(false);
    }
  };

  const loadArtifacts = async () => {
    if (!canQuery) return;

    setArtifactLoading(true);
    try {
      const params = {
        organization_id: selectedOrgId,
        date: selectedDate,
        limit: 100,
      };
      if (artifactType) params.artifact_type = artifactType;
      if (artifactStatus) params.status = artifactStatus;
      if (artifactVisitId) params.visit_id = artifactVisitId;

      const res = await API.get('/super/ai/artifacts', { params });
      setArtifactRows(res.data?.rows || []);
      if (res.data?.server_today_date) setOrgTodayDate(res.data.server_today_date);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || 'Failed to load AI artifacts');
    } finally {
      setArtifactLoading(false);
    }
  };

  const loadVersions = async () => {
    if (!canQuery) return;

    setVersionLoading(true);
    try {
      const params = {
        organization_id: selectedOrgId,
        date: selectedDate,
        limit: 100,
      };
      if (versionType) params.artifact_type = versionType;
      if (versionVisitId) params.visit_id = versionVisitId;

      const res = await API.get('/super/ai/artifact-versions', { params });
      setVersionRows(res.data?.rows || []);
      if (res.data?.server_today_date) setOrgTodayDate(res.data.server_today_date);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || 'Failed to load AI artifact versions');
    } finally {
      setVersionLoading(false);
    }
  };

  const loadJobs = async () => {
    if (!canQuery) return;

    setJobLoading(true);
    try {
      const params = {
        organization_id: selectedOrgId,
        date: selectedDate,
        limit: 100,
      };
      if (jobType) params.artifact_type = jobType;
      if (jobStatus) params.status = jobStatus;
      if (jobVisitId) params.visit_id = jobVisitId;

      const res = await API.get('/super/ai/jobs', { params });
      setJobRows(res.data?.rows || []);
      if (res.data?.server_today_date) setOrgTodayDate(res.data.server_today_date);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || 'Failed to load AI jobs');
    } finally {
      setJobLoading(false);
    }
  };

  useEffect(() => {
    loadOrganizations();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!selectedOrgId) return;
    const org = orgOptions.find((o) => String(o.id) === String(selectedOrgId));
    if (org?.server_today_date) {
      setOrgTodayDate(org.server_today_date);
      setSelectedDate(org.server_today_date);
    }
  }, [selectedOrgId, orgOptions]);

  useEffect(() => {
    if (!canQuery) return;

    if (tab === 'artifacts') loadArtifacts();
    if (tab === 'versions') loadVersions();
    if (tab === 'jobs') loadJobs();
    // eslint-disable-next-line
  }, [tab, selectedOrgId, selectedDate]);

  const artifactStats = useMemo(() => {
    const stats = {
      total: artifactRows.length,
      ready: 0,
      pending: 0,
      failed: 0,
      guidance: 0,
      notes: 0,
    };

    for (const r of artifactRows) {
      const st = String(r.status || '').toLowerCase();
      const tp = String(r.artifact_type || '').toLowerCase();

      if (st === 'ready') stats.ready += 1;
      if (st === 'pending') stats.pending += 1;
      if (st === 'failed') stats.failed += 1;
      if (tp === 'visit_guidance') stats.guidance += 1;
      if (tp === 'visit_note') stats.notes += 1;
    }

    return stats;
  }, [artifactRows]);

  const tabBtnBase =
    'inline-flex items-center justify-center rounded-full border px-3 py-1.5 text-sm font-medium transition';
  const activeTab = 'border-slate-800 bg-slate-800 text-white shadow-sm';
  const inactiveTab =
    'border-slate-300 bg-white text-slate-600 hover:bg-slate-50';

  return (
    <div className="space-y-4 p-4 sm:p-5">
      <div className="overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white shadow-lg">
        <div className="p-5 sm:p-6">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-2xl">
              <div className="inline-flex items-center rounded-full border border-white/10 bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-200">
                AI Operations
              </div>
              <h1 className="mt-3 text-xl font-bold tracking-tight sm:text-2xl">
                Super Admin AI Cache
              </h1>
              <p className="mt-1.5 text-sm leading-6 text-slate-300">
                Centralized view of AI cache, queue jobs, and historical versions
                by organization and visit date.
              </p>
            </div>

            <div className="grid min-w-0 gap-3 sm:grid-cols-2 xl:min-w-[700px] xl:grid-cols-3">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-3">
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-slate-300">
                  Organization
                </label>
                <select
                  className="w-full rounded-xl border border-white/20 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100"
                  value={selectedOrgId}
                  onChange={(e) => setSelectedOrgId(e.target.value)}
                  disabled={orgLoading}
                >
                  <option value="">Select organization</option>
                  {orgOptions.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/10 p-3">
                <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wide text-slate-300">
                  Visit date
                </label>
                <input
                  type="date"
                  className="w-full rounded-xl border border-white/20 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-100 disabled:bg-slate-100 disabled:text-slate-400"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  disabled={!selectedOrgId}
                />
              </div>

              <div className="rounded-2xl border border-white/10 bg-white/10 p-3">
                <div className="text-[11px] font-medium uppercase tracking-wide text-slate-300">
                  Org timezone
                </div>
                <div className="mt-1 truncate text-sm font-semibold text-white">
                  {selectedOrg?.timezone || '-'}
                </div>
                <div className="mt-1 text-xs text-slate-300">
                  Org today: {orgTodayDate || '-'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {!selectedOrgId ? (
        <EmptyState text="Select an organization to load AI cache, versions, and jobs." />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setTab('artifacts')}
              className={`${tabBtnBase} ${tab === 'artifacts' ? activeTab : inactiveTab}`}
            >
              Artifacts
            </button>
            <button
              onClick={() => setTab('versions')}
              className={`${tabBtnBase} ${tab === 'versions' ? activeTab : inactiveTab}`}
            >
              Versions
            </button>
            <button
              onClick={() => setTab('jobs')}
              className={`${tabBtnBase} ${tab === 'jobs' ? activeTab : inactiveTab}`}
            >
              Jobs
            </button>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={async () => {
                try {
                  const res = await API.post('/super/ai/enqueue-guidance-today', {
                    organization_id: Number(selectedOrgId),
                  });
                  alert(`Queued guidance jobs: ${res.data?.queuedCount || 0}`);
                  if (tab === 'jobs') loadJobs();
                } catch (err) {
                  console.error(err);
                  alert(err?.response?.data?.error || 'Failed to enqueue guidance jobs');
                }
              }}
              className={UI.primaryBtn}
              disabled={!selectedOrgId}
            >
              Enqueue Guidance for Org Today
            </button>

            <button
              onClick={async () => {
                try {
                  const res = await API.post('/super/ai/process-guidance-queue', {
                    limit: 5,
                  });
                  alert(`Processed jobs: ${res.data?.processed || 0}`);
                  if (tab === 'jobs') loadJobs();
                  if (tab === 'artifacts') loadArtifacts();
                } catch (err) {
                  console.error(err);
                  alert(err?.response?.data?.error || 'Failed to process queue');
                }
              }}
              className={UI.secondaryBtn}
            >
              Process Queue Now
            </button>

            <button
              onClick={() => {
                if (tab === 'artifacts') loadArtifacts();
                if (tab === 'versions') loadVersions();
                if (tab === 'jobs') loadJobs();
              }}
              className={UI.ghostBtn}
            >
              Refresh Current Tab
            </button>
          </div>

          {tab === 'artifacts' && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <StatCard label="Total" value={artifactStats.total} />
                <StatCard
                  label="Ready"
                  value={artifactStats.ready}
                  valueClassName="text-emerald-700"
                />
                <StatCard
                  label="Pending"
                  value={artifactStats.pending}
                  valueClassName="text-amber-700"
                />
                <StatCard
                  label="Failed"
                  value={artifactStats.failed}
                  valueClassName="text-rose-700"
                />
                <StatCard
                  label="Guidance"
                  value={artifactStats.guidance}
                  valueClassName="text-sky-700"
                />
                <StatCard
                  label="Notes"
                  value={artifactStats.notes}
                  valueClassName="text-indigo-700"
                />
              </div>

              <div className={`${UI.card} p-4`}>
                <div className="grid gap-3 md:grid-cols-4">
                  <select
                    className={UI.select}
                    value={artifactType}
                    onChange={(e) => setArtifactType(e.target.value)}
                  >
                    <option value="">All types</option>
                    <option value="visit_guidance">visit_guidance</option>
                    <option value="visit_note">visit_note</option>
                  </select>

                  <select
                    className={UI.select}
                    value={artifactStatus}
                    onChange={(e) => setArtifactStatus(e.target.value)}
                  >
                    <option value="">All statuses</option>
                    <option value="ready">ready</option>
                    <option value="pending">pending</option>
                    <option value="failed">failed</option>
                    <option value="stale">stale</option>
                  </select>

                  <input
                    className={UI.input}
                    placeholder="Visit ID"
                    value={artifactVisitId}
                    onChange={(e) => setArtifactVisitId(e.target.value)}
                  />

                  <button onClick={loadArtifacts} className={UI.ghostBtn}>
                    {artifactLoading ? 'Loading...' : 'Refresh Artifacts'}
                  </button>
                </div>
              </div>

              <div className={`${UI.card} overflow-auto`}>
                <table className="w-full min-w-[1180px] text-sm">
                  <thead className="bg-slate-50/90">
                    <tr>
                      <th className={UI.th}>Organization</th>
                      <th className={UI.th}>Visit</th>
                      <th className={UI.th}>Visit Scheduled (DB)</th>
                      <th className={UI.th}>Student</th>
                      <th className={UI.th}>Type</th>
                      <th className={UI.th}>Status</th>
                      <th className={UI.th}>Provider / Model</th>
                      <th className={UI.th}>Prompt</th>
                      <th className={UI.th}>Generated</th>
                      <th className={UI.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {artifactRows.map((r) => {
                      const studentLabel =
                        `${r.student_last_name || ''}${r.student_last_name ? ', ' : ''}${r.student_first_name || ''}`.trim();

                      return (
                        <tr
                          key={r.id}
                          className="border-t border-slate-200 hover:bg-slate-50/60"
                        >
                          <td className={UI.td}>
                            <div className="font-semibold text-slate-800">
                              {r.organization_name || '-'}
                            </div>
                            <div className="text-[11px] text-slate-500">
                              #{r.organization_id}
                            </div>
                          </td>
                          <td className={`${UI.td} font-semibold text-slate-800`}>
                            #{r.visit_id}
                          </td>
                          <td className={`${UI.td} text-xs text-slate-700`}>
                            {r.visit_scheduled_at || '-'}
                          </td>
                          <td className={`${UI.td} text-xs text-slate-700`}>
                            {studentLabel || '-'}
                          </td>
                          <td className={UI.td}>
                            <TypeBadge type={r.artifact_type} />
                          </td>
                          <td className={UI.td}>
                            <StatusBadge status={r.status} />
                          </td>
                          <td className={UI.td}>
                            <div className="text-sm text-slate-800">
                              {r.provider || '-'}
                            </div>
                            <div className="text-[11px] text-slate-500">
                              {r.model || '-'}
                            </div>
                          </td>
                          <td className={`${UI.td} text-xs`}>
                            {r.prompt_version || '-'}
                          </td>
                          <td className={`${UI.td} text-xs`}>
                            <div>{r.generated_at || '-'}</div>
                            <div className="text-slate-400">
                              ms: {r.generation_ms || '-'}
                            </div>
                          </td>
                          <td className={UI.td}>
                            <div className="flex flex-wrap gap-1.5">
                              <button
                                onClick={() =>
                                  openJson(`Artifact Result #${r.id}`, r.result_json)
                                }
                                className={UI.smallBtn}
                              >
                                Result
                              </button>
                              <button
                                onClick={() =>
                                  openJson(`Artifact Input #${r.id}`, r.input_json)
                                }
                                className={UI.smallBtn}
                              >
                                Input
                              </button>
                              {r.error_message ? (
                                <button
                                  onClick={() =>
                                    openJson(`Artifact Error #${r.id}`, {
                                      error_message: r.error_message,
                                    })
                                  }
                                  className="inline-flex items-center justify-center rounded-lg border border-rose-300 bg-white px-2.5 py-1.5 text-xs font-medium text-rose-700 transition hover:bg-rose-50"
                                >
                                  Error
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {!artifactRows.length && (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-3 py-10 text-center text-sm text-slate-400"
                        >
                          No artifact rows found for the selected organization and
                          date.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'versions' && (
            <div className="space-y-4">
              <div className={`${UI.card} p-4`}>
                <div className="grid gap-3 md:grid-cols-3">
                  <select
                    className={UI.select}
                    value={versionType}
                    onChange={(e) => setVersionType(e.target.value)}
                  >
                    <option value="">All types</option>
                    <option value="visit_guidance">visit_guidance</option>
                    <option value="visit_note">visit_note</option>
                  </select>

                  <input
                    className={UI.input}
                    placeholder="Visit ID"
                    value={versionVisitId}
                    onChange={(e) => setVersionVisitId(e.target.value)}
                  />

                  <button onClick={loadVersions} className={UI.ghostBtn}>
                    {versionLoading ? 'Loading...' : 'Refresh Versions'}
                  </button>
                </div>
              </div>

              <div className={`${UI.card} overflow-auto`}>
                <table className="w-full min-w-[1120px] text-sm">
                  <thead className="bg-slate-50/90">
                    <tr>
                      <th className={UI.th}>Organization</th>
                      <th className={UI.th}>Visit</th>
                      <th className={UI.th}>Visit Scheduled (DB)</th>
                      <th className={UI.th}>Type</th>
                      <th className={UI.th}>Status</th>
                      <th className={UI.th}>Provider / Model</th>
                      <th className={UI.th}>Prompt</th>
                      <th className={UI.th}>Generated</th>
                      <th className={UI.th}>Source</th>
                      <th className={UI.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {versionRows.map((r) => (
                      <tr
                        key={r.id}
                        className="border-t border-slate-200 hover:bg-slate-50/60"
                      >
                        <td className={UI.td}>
                          <div className="font-semibold text-slate-800">
                            {r.organization_name || '-'}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            #{r.organization_id}
                          </div>
                        </td>
                        <td className={`${UI.td} font-semibold text-slate-800`}>
                          #{r.visit_id}
                        </td>
                        <td className={`${UI.td} text-xs text-slate-700`}>
                          {r.visit_scheduled_at || '-'}
                        </td>
                        <td className={UI.td}>
                          <TypeBadge type={r.artifact_type} />
                        </td>
                        <td className={UI.td}>
                          <StatusBadge status={r.status} />
                        </td>
                        <td className={UI.td}>
                          <div className="text-sm text-slate-800">
                            {r.provider || '-'}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {r.model || '-'}
                          </div>
                        </td>
                        <td className={`${UI.td} text-xs`}>
                          {r.prompt_version || '-'}
                        </td>
                        <td className={`${UI.td} text-xs`}>
                          {r.generated_at || '-'}
                        </td>
                        <td className={`${UI.td} text-xs`}>
                          {r.source_mode || '-'}
                        </td>
                        <td className={UI.td}>
                          <div className="flex flex-wrap gap-1.5">
                            <button
                              onClick={() =>
                                openJson(`Version Result #${r.id}`, r.result_json)
                              }
                              className={UI.smallBtn}
                            >
                              Result
                            </button>
                            <button
                              onClick={() =>
                                openJson(`Version Input #${r.id}`, r.input_json)
                              }
                              className={UI.smallBtn}
                            >
                              Input
                            </button>
                            {r.error_message ? (
                              <button
                                onClick={() =>
                                  openJson(`Version Error #${r.id}`, {
                                    error_message: r.error_message,
                                  })
                                }
                                className="inline-flex items-center justify-center rounded-lg border border-rose-300 bg-white px-2.5 py-1.5 text-xs font-medium text-rose-700 transition hover:bg-rose-50"
                              >
                                Error
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}

                    {!versionRows.length && (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-3 py-10 text-center text-sm text-slate-400"
                        >
                          No version rows found for the selected organization and
                          date.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'jobs' && (
            <div className="space-y-4">
              <div className={`${UI.card} p-4`}>
                <div className="grid gap-3 md:grid-cols-4">
                  <input
                    className={UI.input}
                    placeholder="Visit ID"
                    value={jobVisitId}
                    onChange={(e) => setJobVisitId(e.target.value)}
                  />

                  <select
                    className={UI.select}
                    value={jobType}
                    onChange={(e) => setJobType(e.target.value)}
                  >
                    <option value="">All types</option>
                    <option value="visit_guidance">visit_guidance</option>
                    <option value="visit_note">visit_note</option>
                  </select>

                  <select
                    className={UI.select}
                    value={jobStatus}
                    onChange={(e) => setJobStatus(e.target.value)}
                  >
                    <option value="">All statuses</option>
                    <option value="queued">queued</option>
                    <option value="running">running</option>
                    <option value="done">done</option>
                    <option value="failed">failed</option>
                    <option value="canceled">canceled</option>
                  </select>

                  <button onClick={loadJobs} className={UI.ghostBtn}>
                    {jobLoading ? 'Loading...' : 'Refresh Jobs'}
                  </button>
                </div>
              </div>

              <div className={`${UI.card} overflow-auto`}>
                <table className="w-full min-w-[1080px] text-sm">
                  <thead className="bg-slate-50/90">
                    <tr>
                      <th className={UI.th}>ID</th>
                      <th className={UI.th}>Organization</th>
                      <th className={UI.th}>Visit</th>
                      <th className={UI.th}>Visit Scheduled (DB)</th>
                      <th className={UI.th}>Type</th>
                      <th className={UI.th}>Status</th>
                      <th className={UI.th}>Priority</th>
                      <th className={UI.th}>Trigger</th>
                      <th className={UI.th}>Attempts</th>
                      <th className={UI.th}>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobRows.map((r) => (
                      <tr
                        key={r.id}
                        className="border-t border-slate-200 hover:bg-slate-50/60"
                      >
                        <td className={`${UI.td} font-semibold text-slate-800`}>
                          #{r.id}
                        </td>
                        <td className={UI.td}>
                          <div className="font-semibold text-slate-800">
                            {r.organization_name || '-'}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            #{r.organization_id}
                          </div>
                        </td>
                        <td className={UI.td}>#{r.visit_id}</td>
                        <td className={`${UI.td} text-xs text-slate-700`}>
                          {r.visit_scheduled_at || '-'}
                        </td>
                        <td className={UI.td}>
                          <TypeBadge type={r.artifact_type} />
                        </td>
                        <td className={UI.td}>
                          <StatusBadge status={r.status} />
                        </td>
                        <td className={UI.td}>{r.priority}</td>
                        <td className={`${UI.td} text-xs`}>
                          {r.trigger_source || '-'}
                        </td>
                        <td className={`${UI.td} text-xs`}>
                          {r.attempt_count || 0} / {r.max_attempts || 0}
                        </td>
                        <td className={UI.td}>
                          {r.error_message ? (
                            <button
                              onClick={() =>
                                openJson(`Job Error #${r.id}`, {
                                  error_message: r.error_message,
                                })
                              }
                              className="inline-flex items-center justify-center rounded-lg border border-rose-300 bg-white px-2.5 py-1.5 text-xs font-medium text-rose-700 transition hover:bg-rose-50"
                            >
                              View
                            </button>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}

                    {!jobRows.length && (
                      <tr>
                        <td
                          colSpan={10}
                          className="px-3 py-10 text-center text-sm text-slate-400"
                        >
                          No job rows found for the selected organization and date.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      <JsonModal
        open={jsonModal.open}
        onClose={closeJson}
        title={jsonModal.title}
        data={jsonModal.data}
      />
    </div>
  );
}
